"use client";

import { SettingsRow, SettingsSection } from "@commerce/components/page";
import { useWorkspace } from "@commerce/components/use-workspace";
import { organizationNameSchema } from "@repo/api/lib/validation";
import { authClient } from "@repo/auth/client";
import { organizationsQueryKeys } from "@saas/organizations/lib/api";
import { errorMessage } from "@saas/settings/lib/errors";
import { ImageCropper } from "@shared/components/ImageCropper";
import { useObjectUrl } from "@shared/hooks/use-object-url";
import { orpc } from "@shared/lib/orpc";
import { clearRouteContextCache } from "@shared/lib/route-context-cache";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { Button } from "@ui/components/button";
import { DisabledReason } from "@ui/components/info-tip";
import { Input } from "@ui/components/input";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
	InputGroupText,
} from "@ui/components/input-group";
import { AnchorLinkButton } from "@ui/components/link-button";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import { ArrowUpRightIcon, ImageUpIcon } from "lucide-react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useActiveOrganization } from "../hooks/use-active-organization";
import { OrganizationLogo } from "./OrganizationLogo";

/** Logo, name and web address of the retailer. */
export function OrganizationProfileSection() {
	const { organization } = useWorkspace();

	if (!organization) {
		return null;
	}

	return (
		<SettingsSection
			title="Retailer profile"
			info="How this retailer appears to your team and on the hosted shopper chat."
		>
			<LogoRow
				organizationId={organization.id}
				name={organization.name}
				logo={organization.logo ?? null}
			/>
			{/* Keyed by name so the field follows a rename made elsewhere. */}
			<NameRow
				key={organization.name}
				organizationId={organization.id}
				initialName={organization.name}
			/>
			<SettingsRow
				label="Web address"
				info="Set when the retailer was created. Ask Hephon to change it: links shared with shoppers would break."
			>
				<InputGroup>
					<InputGroupAddon>
						<InputGroupText>/c/</InputGroupText>
					</InputGroupAddon>
					<InputGroupInput
						value={organization.slug}
						readOnly
						aria-label="Web address"
						className="font-mono"
					/>
				</InputGroup>
				<AnchorLinkButton
					variant="outline"
					size="sm"
					href={`/c/${organization.slug}`}
					target="_blank"
					rel="noreferrer"
				>
					Open the hosted chat
					<ArrowUpRightIcon data-icon="inline-end" />
				</AnchorLinkButton>
			</SettingsRow>
		</SettingsSection>
	);
}

function useRefreshOrganization() {
	const queryClient = useQueryClient();
	const router = useRouter();
	const { refetchActiveOrganization } = useActiveOrganization();
	return async () => {
		await Promise.all([
			refetchActiveOrganization(),
			queryClient.invalidateQueries({
				queryKey: organizationsQueryKeys.list(),
			}),
		]);
		// The sidebar and route context read the server-loaded organization.
		clearRouteContextCache();
		await router.invalidate();
	};
}

function LogoRow({
	organizationId,
	name,
	logo,
}: {
	organizationId: string;
	name: string;
	logo: string | null;
}) {
	const refresh = useRefreshOrganization();
	const [uploading, setUploading] = useState(false);
	const [picked, setPicked] = useState<File | null>(null);
	const imageUrl = useObjectUrl(picked);
	const uploadUrl = useMutation(
		orpc.organizations.createLogoUploadUrl.mutationOptions(),
	);

	const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
		onDrop: ([file]) => {
			if (file) {
				setPicked(file);
			}
		},
		accept: { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"] },
		multiple: false,
		noClick: true,
	});

	const upload = async (image: Blob) => {
		setUploading(true);
		try {
			const { signedUploadUrl, path } = await uploadUrl.mutateAsync({
				organizationId,
			});
			const response = await fetch(signedUploadUrl, {
				method: "PUT",
				body: image,
				headers: { "Content-Type": "image/png" },
			});
			if (!response.ok) {
				throw new Error("The image upload failed. Try again.");
			}
			const { error } = await authClient.organization.update({
				organizationId,
				data: { logo: path },
			});
			if (error) {
				throw error;
			}
			await refresh();
			toast.add({ type: "success", title: "Logo updated" });
		} catch (error) {
			toast.add({
				type: "error",
				title: "Could not update the logo",
				description: errorMessage(error),
			});
		} finally {
			setUploading(false);
		}
	};

	return (
		<SettingsRow
			label="Logo"
			description="Square PNG or JPG."
			info="Shown in the sidebar and at the top of the shopper chat."
		>
			<div
				{...getRootProps({
					className:
						"flex items-center gap-4 rounded-2xl sm:justify-end outline-none",
				})}
			>
				<input {...getInputProps()} />
				<div className="relative">
					<OrganizationLogo
						name={name}
						logoUrl={logo}
						className="size-14 text-lg"
					/>
					{uploading || isDragActive ? (
						<span className="absolute inset-0 grid place-items-center rounded-lg bg-background/80">
							{uploading ? (
								<Spinner />
							) : (
								<ImageUpIcon className="size-5" />
							)}
						</span>
					) : null}
				</div>
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={open}
					disabled={uploading}
				>
					{logo ? "Change logo" : "Upload logo"}
				</Button>
			</div>
			{imageUrl ? (
				<ImageCropper
					image={imageUrl}
					open
					onClose={() => setPicked(null)}
					onCropComplete={(image) => {
						setPicked(null);
						void upload(image);
					}}
					aspect={1}
					cropShape="rect"
					title="Crop the logo"
					compression={{ maxSizeMB: 0.1, maxWidthOrHeight: 256 }}
				/>
			) : null}
		</SettingsRow>
	);
}

function NameRow({
	organizationId,
	initialName,
}: {
	organizationId: string;
	/** The saved name; the row is keyed by it, so a rename elsewhere resets the field. */
	initialName: string;
}) {
	const refresh = useRefreshOrganization();
	const [value, setValue] = useState(initialName);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const dirty = value.trim() !== initialName;

	const save = async () => {
		const parsed = organizationNameSchema.safeParse(value.trim());
		if (!parsed.success) {
			setError(parsed.error.issues[0]?.message ?? "Enter a valid name");
			return;
		}
		setSaving(true);
		try {
			const { error } = await authClient.organization.update({
				organizationId,
				data: { name: value.trim() },
			});
			if (error) {
				throw error;
			}
			await refresh();
			toast.add({ type: "success", title: "Name updated" });
		} catch (error) {
			toast.add({
				type: "error",
				title: "Could not rename the retailer",
				description: errorMessage(error),
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<SettingsRow
			label="Name"
			htmlFor="organization-name"
			info="Your team sees this everywhere; shoppers see it in the chat header."
		>
			<form
				className="flex w-full flex-col gap-2 sm:flex-row"
				onSubmit={(event) => {
					event.preventDefault();
					void save();
				}}
			>
				<Input
					id="organization-name"
					value={value}
					onChange={(event) => {
						setValue(event.target.value);
						setError(null);
					}}
					aria-invalid={error ? true : undefined}
					maxLength={60}
					className="flex-1"
				/>
				<DisabledReason
					reason={!dirty && !saving && "No changes to save"}
				>
					<Button type="submit" disabled={!dirty || saving}>
						{saving ? <Spinner data-icon="inline-start" /> : null}
						Save
					</Button>
				</DisabledReason>
			</form>
			{error ? (
				<p role="alert" className="text-destructive text-sm">
					{error}
				</p>
			) : null}
		</SettingsRow>
	);
}
